import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, InputAdornment, InputBase, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import * as XLSX from "xlsx";
import clienteServicioService from "../services/clienteServicioService";

// Reporte de mantenimientos de módems por requerimiento (AVL): se elige un rango de fechas del REQ (de registro o de
// culminación) y se listan los REQ con los módems a los que se les hizo mantenimiento. Se exporta a Excel una fila por
// módem, con los datos del REQ repetidos.

const ESTADOS_REQ = {
  "1": "Coordinación", "2": "Programación", "3": "Trabajo de campo", "4": "Trabajo de campo",
  "5": "Finalizado", "7": "Revisión en Sistemas", "8": "Finalizado",
};
const estadoReq = (e) => ESTADOS_REQ[String(e)] || (e ? `Estado ${e}` : "—");
const colorEstadoReq = (e) => (["5", "8"].includes(String(e)) ? "success" : String(e) === "7" ? "info" : "warning");

const dos = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
const fecha = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) || d.getFullYear() < 1950 ? "—" : `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// Rangos rápidos (relativos a hoy).
const RANGOS = [
  { clave: "mes", texto: "Este mes", calc: (h) => [new Date(h.getFullYear(), h.getMonth(), 1), h] },
  { clave: "anterior", texto: "Mes anterior", calc: (h) => [new Date(h.getFullYear(), h.getMonth() - 1, 1), new Date(h.getFullYear(), h.getMonth(), 0)] },
  { clave: "tres", texto: "Últimos 3 meses", calc: (h) => [new Date(h.getFullYear(), h.getMonth() - 2, 1), h] },
  { clave: "anio", texto: "Este año", calc: (h) => [new Date(h.getFullYear(), 0, 1), h] },
];

const COLOR_OPERACION = (op = "") => (/remplazo|reemplazo/i.test(op) ? "warning" : /adicion/i.test(op) ? "success" : /remoto/i.test(op) ? "info" : "default");

// Totales en una sola franja, separados por líneas; en 0 se atenúan.
function Totales({ items }) {
  return (
    <Stack direction="row" sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflow: "hidden", flexShrink: 0 }}>
      {items.map(({ titulo, valor }, i) => (
        <Box key={titulo} sx={{ px: 1.5, py: 0.6, borderLeft: i ? 1 : 0, borderColor: "divider", minWidth: 92 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3 }}>{titulo}</Typography>
          <Typography sx={{ fontSize: 17, fontWeight: 900, lineHeight: 1.25, fontVariantNumeric: "tabular-nums", color: valor ? "text.primary" : "text.disabled" }}>{valor}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

// Rango de fechas compacto: las dos fechas dentro de un mismo recuadro.
function RangoFechas({ desde, hasta, onChange }) {
  const inputSx = { fontSize: 13, fontWeight: 700, width: 128, "& input": { py: 0.6, px: 0.5, fontVariantNumeric: "tabular-nums" } };
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}
      sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, px: 1, bgcolor: "background.paper", "&:focus-within": { borderColor: "primary.main" } }}>
      <EventOutlinedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
      <InputBase type="date" value={desde} onChange={(e) => onChange({ desde: e.target.value })} inputProps={{ "aria-label": "Desde" }} sx={inputSx} />
      <ArrowForwardIcon sx={{ fontSize: 15, color: "text.disabled" }} />
      <InputBase type="date" value={hasta} onChange={(e) => onChange({ hasta: e.target.value })} inputProps={{ "aria-label": "Hasta" }} sx={inputSx} />
    </Stack>
  );
}

const segmentadoSx = {
  "& .MuiToggleButton-root": { textTransform: "none", fontSize: 12, fontWeight: 700, py: 0.45, px: 1.2, lineHeight: 1.4 },
  "& .MuiToggleButton-root.Mui-selected": { bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" } },
};

function FilaReq({ req, abierto, onToggle }) {
  return (
    <>
      <TableRow hover onClick={onToggle} sx={{ cursor: "pointer", "& > td": { borderBottom: abierto ? "none" : undefined } }}>
        <TableCell sx={{ width: 36, p: "0 4px !important" }}>
          <IconButton size="small" aria-label={abierto ? "Ocultar módems" : "Ver módems"}>
            <KeyboardArrowDownIcon fontSize="small" sx={{ transform: abierto ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 800 }}>{req.cod}</TableCell>
        <TableCell>{fecha(req.fecha_req)}</TableCell>
        <TableCell>{fecha(req.fecha_culminacion)}</TableCell>
        <TableCell>{req.tipo || "—"}</TableCell>
        <TableCell><Chip size="small" variant="outlined" color={colorEstadoReq(req.estado)} label={estadoReq(req.estado)} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} /></TableCell>
        <TableCell>{req.solicitante || "—"}</TableCell>
        <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={req.observacion || ""}>{req.observacion || "—"}</TableCell>
        <TableCell align="right"><Chip size="small" color="primary" label={req.modems.length} sx={{ height: 20, fontWeight: 800 }} /></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: "0 !important", borderBottom: abierto ? undefined : "none" }}>
          <Collapse in={abierto} unmountOnExit>
            <Box sx={{ m: 1, ml: 5.5, border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.4, px: 0.9, fontSize: 11.5 }, "& thead .MuiTableCell-root": { fontWeight: 800, bgcolor: "action.hover" } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Placa</TableCell>
                    <TableCell>Serie / IMEI</TableCell>
                    <TableCell>Alias</TableCell>
                    <TableCell>Modelo</TableCell>
                    <TableCell>Operación</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Ciudad</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Observación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {req.modems.map((m, i) => (
                    <TableRow key={`${m.serie}-${i}`}>
                      <TableCell sx={{ fontWeight: 700 }}>{m.placa || "—"}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{m.serie}{m.emai && m.emai !== m.serie ? ` · ${m.emai}` : ""}</TableCell>
                      <TableCell>{m.id_ref || "—"}</TableCell>
                      <TableCell>{m.modelo || "—"}</TableCell>
                      <TableCell><Chip size="small" variant="outlined" color={COLOR_OPERACION(m.operacion)} label={m.operacion} sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} /></TableCell>
                      <TableCell>{m.estado || "—"}</TableCell>
                      <TableCell>{fecha(m.fecha)}</TableCell>
                      <TableCell>{m.ciudad || "—"}</TableCell>
                      <TableCell>{m.usu || "—"}</TableCell>
                      <TableCell sx={{ maxWidth: 280, whiteSpace: "normal", wordBreak: "break-word" }}>{m.observacion || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function MantenimientosPorReq({ codServicio, nombreCliente = "" }) {
  const [rango, setRango] = useState(() => {
    const [d, h] = RANGOS[0].calc(new Date());
    return { desde: iso(d), hasta: iso(h), clave: "mes" };
  });
  const [campo, setCampo] = useState("registro");
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [filtro, setFiltro] = useState("");
  const [abiertos, setAbiertos] = useState(() => new Set());

  const cargar = useCallback(async () => {
    if (!codServicio || !rango.desde || !rango.hasta) return;
    if (rango.desde > rango.hasta) { setError("La fecha \"desde\" no puede ser posterior a \"hasta\"."); return; }
    setCargando(true);
    setError("");
    try {
      const r = await clienteServicioService.getMantenimientosPorReq(codServicio, { desde: rango.desde, hasta: rango.hasta, campo });
      setDatos(r);
      setAbiertos(new Set());
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cargar el reporte de mantenimientos.");
    } finally {
      setCargando(false);
    }
  }, [codServicio, rango.desde, rango.hasta, campo]);

  useEffect(() => { cargar(); }, [cargar]);

  const elegirRango = (clave) => {
    const r = RANGOS.find((x) => x.clave === clave);
    if (!r) return;
    const [d, h] = r.calc(new Date());
    setRango({ desde: iso(d), hasta: iso(h), clave });
  };

  // Filtro local por REQ, placa, serie, alias, solicitante u observación.
  const reqs = useMemo(() => {
    const lista = datos?.reqs || [];
    const q = filtro.trim().toLowerCase();
    if (!q) return lista;
    return lista
      .map((r) => {
        const delReq = [r.cod, r.solicitante, r.observacion, r.tipo].some((v) => String(v ?? "").toLowerCase().includes(q));
        if (delReq) return r;
        const modems = r.modems.filter((m) => [m.placa, m.serie, m.emai, m.id_ref, m.modelo, m.observacion, m.usu].some((v) => String(v ?? "").toLowerCase().includes(q)));
        return modems.length ? { ...r, modems } : null;
      })
      .filter(Boolean);
  }, [datos, filtro]);

  const todosAbiertos = reqs.length > 0 && reqs.every((r) => abiertos.has(r.cod));
  const alternar = (cod) => setAbiertos((prev) => {
    const s = new Set(prev);
    if (s.has(cod)) s.delete(cod); else s.add(cod);
    return s;
  });

  const exportar = () => {
    const filas = reqs.flatMap((r) => r.modems.map((m) => ({
      REQ: r.cod,
      "Fecha REQ": fecha(r.fecha_req),
      "Fecha culminación": fecha(r.fecha_culminacion),
      "Tipo REQ": r.tipo || "",
      "Estado REQ": estadoReq(r.estado),
      Solicitante: r.solicitante || "",
      "Observación REQ": r.observacion || "",
      Placa: m.placa || "",
      Serie: m.serie || "",
      IMEI: m.emai || "",
      Alias: m.id_ref || "",
      Modelo: m.modelo || "",
      Operación: m.operacion || "",
      "Estado módem": m.estado || "",
      "Fecha mantenimiento": fecha(m.fecha),
      Ciudad: m.ciudad || "",
      Usuario: m.usu || "",
      Observación: m.observacion || "",
    })));
    const ws = XLSX.utils.json_to_sheet(filas);
    ws["!cols"] = Object.keys(filas[0] || {}).map((k) => ({ wch: Math.min(Math.max(k.length + 2, ...filas.map((f) => String(f[k] ?? "").length)) + 1, 45) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mantenimientos");
    const nombre = String(nombreCliente || codServicio).replace(/[\\/:*?"<>|]+/g, "").trim().replace(/\s+/g, "_");
    XLSX.writeFile(wb, `mantenimientos_${nombre}_${rango.desde}_a_${rango.hasta}.xlsx`);
  };

  const resumen = datos?.resumen;
  const totalModems = reqs.reduce((s, r) => s + r.modems.length, 0);
  const desdeTexto = fecha(`${rango.desde}T12:00:00`);
  const hastaTexto = fecha(`${rango.hasta}T12:00:00`);
  const textoCampo = campo === "culminacion" ? "culminación" : "registro";
  // En la lista vacía se sugiere el siguiente rango más amplio.
  const rangoMasAmplio = ["mes", "anterior", ""].includes(rango.clave)
    ? RANGOS.find((r) => r.clave === "tres")
    : rango.clave === "tres" ? RANGOS.find((r) => r.clave === "anio") : null;

  return (
    <Paper variant="outlined" sx={{ mt: 1, borderRadius: 1.5, overflow: "hidden" }}>
      {/* Encabezado: título + exportar */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", flexShrink: 0, color: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }}>
          <BuildCircleOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 900 }}>Mantenimientos por requerimiento</Typography>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }} noWrap>
            {desdeTexto} – {hastaTexto} · por fecha de {textoCampo} del REQ
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={exportar} disabled={!totalModems || cargando}
          sx={{ textTransform: "none", fontWeight: 800, flexShrink: 0 }}>
          Exportar Excel
        </Button>
      </Stack>

      {/* Filtros */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap columnGap={1.25} rowGap={1}
        sx={{ px: 1.5, py: 1, bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.06 : 0.03), borderTop: 1, borderBottom: 1, borderColor: "divider" }}>
        <ToggleButtonGroup size="small" exclusive value={campo} onChange={(_, v) => v && setCampo(v)} sx={segmentadoSx}>
          <ToggleButton value="registro">Fecha de registro</ToggleButton>
          <ToggleButton value="culminacion">Fecha de culminación</ToggleButton>
        </ToggleButtonGroup>
        <RangoFechas desde={rango.desde} hasta={rango.hasta} onChange={(c) => setRango((r) => ({ ...r, ...c, clave: "" }))} />
        <ToggleButtonGroup size="small" exclusive value={rango.clave || null} onChange={(_, v) => v && elegirRango(v)} sx={segmentadoSx}>
          {RANGOS.map((r) => <ToggleButton key={r.clave} value={r.clave}>{r.texto}</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>

      {error && <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ p: 4, display: "grid", placeItems: "center" }}><CircularProgress size={26} /></Box>
      ) : datos && !datos.reqs.length ? (
        <Stack alignItems="center" spacing={0.75} sx={{ py: 4, px: 2, textAlign: "center" }}>
          <EventBusyOutlinedIcon sx={{ fontSize: 38, color: "text.disabled" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Sin mantenimientos en este rango</Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", maxWidth: 440 }}>
            Ningún requerimiento de este cliente con fecha de {textoCampo} entre {desdeTexto} y {hastaTexto} tiene mantenimientos de módems.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
            {rangoMasAmplio && (
              <Button size="small" variant="contained" onClick={() => elegirRango(rangoMasAmplio.clave)} sx={{ textTransform: "none", fontWeight: 800 }}>
                Ver {rangoMasAmplio.texto.toLowerCase()}
              </Button>
            )}
            {campo === "registro" && (
              <Button size="small" onClick={() => setCampo("culminacion")} sx={{ textTransform: "none", fontWeight: 700 }}>
                Buscar por fecha de culminación
              </Button>
            )}
          </Stack>
        </Stack>
      ) : datos && (
        <>
          <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap columnGap={1.5} rowGap={1} sx={{ px: 1.5, py: 1.1 }}>
            <Totales items={[
              { titulo: "Requerimientos", valor: resumen.reqs },
              { titulo: "Mantenimientos", valor: resumen.mantenimientos },
              { titulo: "Módems", valor: resumen.modems },
              { titulo: "Placas", valor: resumen.placas },
            ]} />
            <Box sx={{ flex: 1 }} />
            <TextField size="small" placeholder="Buscar REQ, placa, serie, alias…" value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ width: 260 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>, sx: { fontSize: 13 } }} />
            <Button size="small" startIcon={todosAbiertos ? <UnfoldLessIcon /> : <UnfoldMoreIcon />} disabled={!reqs.length}
              onClick={() => setAbiertos(todosAbiertos ? new Set() : new Set(reqs.map((r) => r.cod)))} sx={{ textTransform: "none", fontWeight: 700 }}>
              {todosAbiertos ? "Contraer todos" : "Expandir todos"}
            </Button>
          </Stack>
          {resumen.truncado && (
            <Alert severity="warning" sx={{ mx: 1.5, mb: 1 }}>Hay más de 5000 mantenimientos en el rango: se muestran los primeros. Acorta el rango de fechas.</Alert>
          )}

          {!reqs.length ? (
            <Typography sx={{ px: 1.5, pb: 2, fontSize: 12.5, color: "text.secondary" }}>
              Ningún requerimiento o módem coincide con "{filtro}".
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto", maxHeight: "65vh" }}>
              <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.5, px: 0.9, fontSize: 12 } }}>
                <TableHead>
                  <TableRow sx={{ "& .MuiTableCell-root": { fontWeight: 800, fontSize: 11.5, bgcolor: "background.paper" } }}>
                    <TableCell />
                    <TableCell>REQ</TableCell>
                    <TableCell>Registro</TableCell>
                    <TableCell>Culminación</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Solicitante</TableCell>
                    <TableCell>Observación</TableCell>
                    <TableCell align="right">Módems</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reqs.map((r) => <FilaReq key={r.cod} req={r} abierto={abiertos.has(r.cod)} onToggle={() => alternar(r.cod)} />)}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
